import { InAppMessage, InAppMessageType, UpdatePreferenceRequest } from '@quick/client';
import { notificationApi } from '../api';
import { useCurrentActiveMember, useCurrentMember } from '../redux/selector';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { inAppMessagesQueryKey, notificationPreferenceQueryKey } from '../api/query';
import { useEffect } from 'react';
import { DEFAULT_REFRESH_INTERVAL } from '../constants/ForestConstant';

export const useInAppMessagesQuery = () => {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const currentMember = useCurrentMember()!;
  // Fetch all.
  return useQuery(
    inAppMessagesQueryKey(currentMember.communityId),
    () =>
      notificationApi.listUnreadInAppMessages({
        communityId: currentMember.communityId,
        messageTypes: [],
      }),
    {
      refetchOnWindowFocus: true,
      refetchInterval: DEFAULT_REFRESH_INTERVAL,
      enabled: !!currentMember,
    }
  );
};

export const useInAppMessagesMutation = () => {
  const queryClient = useQueryClient();
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const currentMember = useCurrentMember()!;
  const markMessageIdsAsRead = useMutation(
    (messageIds: number[]) =>
      notificationApi.clearInAppMessages({
        markInAppMessagesAsRead: {
          communityId: currentMember.communityId,
          inAppMessageIds: messageIds,
          messageTypes: [],
        },
      }),
    {
      onSuccess(data, messageIds: number[]) {
        // Optimistically update the query result.
        queryClient.setQueryData<InAppMessage[] | undefined>(inAppMessagesQueryKey(currentMember.communityId), (old) =>
          old?.filter((it) => !messageIds.includes(it.inAppMessageId))
        );
        // Invalidate the query.
        queryClient.invalidateQueries(inAppMessagesQueryKey(currentMember.communityId));
      },
    }
  );
  const markMessageTypesAsRead = useMutation(
    (messageTypes: InAppMessageType[]) =>
      notificationApi.clearInAppMessages({
        markInAppMessagesAsRead: {
          communityId: currentMember.communityId,
          inAppMessageIds: [],
          messageTypes: messageTypes,
        },
      }),
    {
      onSuccess(data, messageTypes: InAppMessageType[]) {
        // Optimistically update the query result.
        queryClient.setQueryData<InAppMessage[] | undefined>(inAppMessagesQueryKey(currentMember.communityId), (old) =>
          old?.filter((it) => !messageTypes.includes(it.messageEnvelope.messageType))
        );
        // Invalidate the query.
        queryClient.invalidateQueries(inAppMessagesQueryKey(currentMember.communityId));
      },
    }
  );
  return { markMessageIdsAsRead, markMessageTypesAsRead };
};

export const usePrefetchNotificationPreference = () => {
  const currentMember = useCurrentActiveMember();
  const communityId = currentMember?.communityId;
  const queryClient = useQueryClient();
  useEffect(() => {
    if (!communityId) {
      return;
    }
    queryClient.prefetchQuery(notificationPreferenceQueryKey(communityId), () =>
      notificationApi.getPreference({
        communityId,
      })
    );
  }, [communityId, queryClient]);
};

export const useNotificationPreferenceQuery = () => {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const currentMember = useCurrentMember()!;
  return useQuery(notificationPreferenceQueryKey(currentMember.communityId), () =>
    notificationApi.getPreference({
      communityId: currentMember.communityId,
    })
  );
};

export const useNotificationPreferenceMutation = () => {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const currentMember = useCurrentMember()!;
  const queryClient = useQueryClient();
  const updatePreference = useMutation((args: UpdatePreferenceRequest) => notificationApi.updatePreference(args), {
    onSuccess() {
      queryClient.invalidateQueries(notificationPreferenceQueryKey(currentMember.communityId));
    },
  });
  return {
    updatePreference,
  };
};
